import { useState } from 'react';
import type { AppliesTo, RepCriterionDto } from '@coachrex/shared';
import { useRepCriterionMutations, useRepScoring } from '../lib/api';
import { scoreColor } from '../lib/format';
import { ErrorBox, PageHeader, Pills, Skeleton } from '../components/ui';
import { Modal } from '../components/Modal';
import './scoring.css';

const APPLIES: { value: AppliesTo; label: string }[] = [
  { value: 'all', label: 'All calls' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
];

/** Handoff screen 08: the criteria every call is scored against. */
export function RepScoring() {
  const { data, error, isLoading } = useRepScoring();
  const m = useRepCriterionMutations();
  const [editing, setEditing] = useState<RepCriterionDto | 'new' | null>(null);

  const crit = data?.criteria ?? [];
  const total = crit.filter((c) => c.enabled).reduce((a, c) => a + c.weight, 0);
  const busy = m.update.isPending || m.remove.isPending;

  return (
    <div className="page">
      <PageHeader
        title="Rep scoring"
        subtitle={
          <>
            Every call is scored against these. Texts and emails are tracked for reply time but not scored yet. Active weights:{' '}
            <b style={{ color: total === 100 ? 'var(--score-good)' : 'var(--score-bad)' }}>{total}%</b> of 100%.
          </>
        }
        actions={
          <button className="btn btn--primary" onClick={() => setEditing('new')}>
            Add criterion
          </button>
        }
      />
      {total !== 100 && crit.length > 0 && (
        <div className="hint-box">Scores are always out of 100: weights are scaled to the criteria that are on. Getting them to total 100% just makes each weight read as a share of the score.</div>
      )}

      <section className="card card--list">
        <div className="list-head crit-grid">
          <span>On</span>
          <span>Criterion</span>
          <span style={{ textAlign: 'right' }}>Weight</span>
          <span style={{ textAlign: 'right' }}>Team pass rate</span>
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={44} />
            ))}
          </div>
        ) : (
          crit.map((c) => (
            <div key={c.id} className="list-row crit-grid" style={{ opacity: c.enabled ? 1 : 0.5, padding: '13px 12px' }}>
              <span>
                <button
                  className={`toggle${c.enabled ? ' is-on' : ''}`}
                  role="switch"
                  aria-checked={c.enabled}
                  aria-label={`Score calls on ${c.name}`}
                  disabled={busy}
                  onClick={() => m.update.mutate({ id: c.id, enabled: !c.enabled })}
                />
              </span>
              <button className="crit-name" onClick={() => setEditing(c)}>
                <span style={{ fontWeight: 600 }}>
                  {c.name}
                  {c.appliesTo !== 'all' && <span className="crit-tag">{c.appliesTo === 'inbound' ? 'Inbound only' : 'Outbound only'}</span>}
                </span>
                <span className="crit-desc">{c.passDefinition}</span>
              </button>
              <span className="crit-weight">
                <button aria-label={`Lower ${c.name} weight`} disabled={busy || c.weight <= 0} onClick={() => m.update.mutate({ id: c.id, weight: Math.max(0, c.weight - 5) })}>
                  −
                </button>
                <b>{c.weight}%</b>
                <button aria-label={`Raise ${c.name} weight`} disabled={busy || c.weight >= 40} onClick={() => m.update.mutate({ id: c.id, weight: Math.min(40, c.weight + 5) })}>
                  +
                </button>
              </span>
              <span style={{ textAlign: 'right', fontWeight: 600, color: c.passRate == null ? 'var(--faint)' : scoreColor(c.passRate) }} title={`${c.scoredCalls} calls scored`}>
                {c.passRate == null ? '—' : `${c.passRate}%`}
              </span>
            </div>
          ))
        )}
      </section>
      <span className="footnote-text">
        Turning criteria on or off and changing weights re-scores past calls right away, at no AI cost. New or reworded criteria apply to calls analyzed from now on.
        {data && ` ${data.analyzedCalls90d.toLocaleString('en-US')} calls analyzed in the last 90 days.`}
      </span>
      {(m.update.error || m.remove.error) && <ErrorBox error={m.update.error ?? m.remove.error} />}

      {editing && <CriterionModal criterion={editing === 'new' ? null : editing} activeTotal={total} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CriterionModal({ criterion, activeTotal, onClose }: { criterion: RepCriterionDto | null; activeTotal: number; onClose: () => void }) {
  const m = useRepCriterionMutations();
  const [name, setName] = useState(criterion?.name ?? '');
  const [desc, setDesc] = useState(criterion?.passDefinition ?? '');
  const [weight, setWeight] = useState(criterion?.weight ?? 10);
  const [applies, setApplies] = useState<AppliesTo>(criterion?.appliesTo ?? 'all');

  // Live total as if this change were saved (only enabled criteria count).
  const after = activeTotal - (criterion?.enabled ? criterion.weight : 0) + (criterion && !criterion.enabled ? 0 : weight);
  const canSave = name.trim() && desc.trim() && !m.add.isPending && !m.update.isPending;
  const save = () => {
    if (!canSave) return;
    const body = { name: name.trim(), passDefinition: desc.trim(), weight, appliesTo: applies };
    if (criterion) m.update.mutate({ id: criterion.id, ...body }, { onSuccess: onClose });
    else m.add.mutate(body, { onSuccess: onClose });
  };

  return (
    <Modal
      title={criterion ? 'Edit criterion' : 'New scoring criterion'}
      subtitle={criterion ? 'Wording changes apply to calls analyzed from now on.' : 'Coach Lex checks every call for this and marks it pass or fail.'}
      onClose={onClose}
      width={520}
    >
      <label className="form-label">
        <span>Name</span>
        <input className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Offered a site visit" autoFocus />
      </label>
      <label className="form-label">
        <span>What counts as a pass</span>
        <textarea className="text-input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Rep offers to book an on-site consultation before the end of the call." />
      </label>
      <div className="form-label">
        <span style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Weight</span>
          <span>{weight}%</span>
        </span>
        <input type="range" min={0} max={40} step={5} value={weight} onChange={(e) => setWeight(Number(e.target.value))} style={{ accentColor: 'var(--accent)' }} aria-label="Weight" />
        <span style={{ fontSize: 12, color: after === 100 ? 'var(--score-good)' : 'var(--faint)' }}>
          {after === 100 ? 'Active weights will total 100%.' : `Active weights will total ${after}%. Adjust others to reach 100%.`}
        </span>
      </div>
      <div className="form-label">
        <span>Applies to</span>
        <Pills options={APPLIES} value={applies} onChange={setApplies} />
      </div>
      {(m.add.error || m.update.error) && <ErrorBox error={m.add.error ?? m.update.error} />}
      <div className="modal__actions" style={{ justifyContent: criterion ? 'space-between' : 'flex-end' }}>
        {criterion && (
          <button
            className="btn btn--secondary"
            style={{ color: 'var(--red-fg)' }}
            disabled={m.remove.isPending}
            onClick={() => {
              if (confirm(`Remove "${criterion.name}"? Past scores are recalculated without it.`)) m.remove.mutate(criterion.id, { onSuccess: onClose });
            }}
          >
            Remove
          </button>
        )}
        <span style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--accent" disabled={!canSave} onClick={save}>
            {criterion ? 'Save' : 'Add criterion'}
          </button>
        </span>
      </div>
    </Modal>
  );
}
